import produce from "immer";
import {
  DELETE_STARTED,
  DELETE_QUEUED,
  DELETE_PROJECT_STARTED_JOB,
  DELETE_PROJECT_COMPLETED,
  DELETE_PROJECT_FAILED,
  DELETE_ALL_COMPLETED,
  DELETE_ALL_FAILED,
  DELETE_JOB_DISMISS,
  DELETE_JOB_MODAL_TOGGLE,
  DELETE_REQUEST_FAILED,
} from "../types";

/**
 * Mirrors the shape of the import slice so the shared progress dialog can bind to
 * either without special-casing. The state-machine semantics are identical —
 * "projects" here just happens to be a single-entry list in today's single-project
 * delete flow, but the structure is ready for a future bulk-delete.
 */
const PROJECT_STATUS = {
  QUEUED: "QUEUED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
};

const initialJobState = {
  active: false,
  open: false,
  startedAt: null,
  terminatedAt: null,
  fileName: null, // unused for delete but kept for shape parity
  totalProjects: 0,
  completedCount: 0,
  failedCount: 0,
  etaSeconds: null,
  avgSecondsPerWeight: null,
  error: null,
  projects: [],
};

const initialState = { jobs: {} };

function upsertProject(job, itemIndex, patch) {
  if (itemIndex == null) return;
  const idx = job.projects.findIndex((p) => p.index === itemIndex);
  if (idx === -1) {
    job.projects.push({
      index: itemIndex,
      label: patch.label ?? "",
      status: PROJECT_STATUS.QUEUED,
      weight: 1,
      startedAt: null,
      completedAt: null,
      error: null,
      ...patch,
    });
    return;
  }
  Object.assign(job.projects[idx], patch);
}

export default function deleteReducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case DELETE_STARTED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        // Merge onto an existing job so a re-dispatched STARTED (rehydration)
        // updates the provided fields without zeroing the rest.
        const prev = draft.jobs[jobId];
        draft.jobs[jobId] = {
          ...initialJobState,
          ...(prev ?? {}),
          ...payload,
          active: true,
          open: true,
          startedAt: prev?.startedAt ?? payload?.startedAt ?? Date.now(),
        };
      });

    case DELETE_QUEUED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = true;
        job.totalProjects = payload.total ?? (payload.projects?.length ?? 0);
        if (payload.avgSecondsPerWeight != null) {
          job.avgSecondsPerWeight = payload.avgSecondsPerWeight;
        }
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
        job.projects = (payload.projects || []).map((p) => ({
          index: p.index,
          label: p.label,
          kind: p.kind ?? null,
          name: p.name ?? null,
          weight: p.weight ?? 1,
          status: PROJECT_STATUS.QUEUED,
          startedAt: null,
          completedAt: null,
          error: null,
        }));
      });

    case DELETE_PROJECT_STARTED_JOB:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.IN_PROGRESS,
          startedAt: payload.startedAt ?? Date.now(),
        });
      });

    case DELETE_PROJECT_COMPLETED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.COMPLETED,
          startedAt: payload.startedAt ?? job.projects[payload.index]?.startedAt ?? null,
          completedAt: payload.completedAt ?? Date.now(),
          error: null,
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case DELETE_PROJECT_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        upsertProject(job, payload.index, {
          label: payload.label,
          weight: payload.weight ?? 1,
          status: PROJECT_STATUS.FAILED,
          startedAt: payload.startedAt ?? job.projects[payload.index]?.startedAt ?? null,
          completedAt: payload.completedAt ?? Date.now(),
          error: payload.error || "Unknown error",
          errorCode: payload.errorCode ?? null,
          errorParams: payload.errorParams ?? null,
        });
        job.completedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.COMPLETED
        ).length;
        job.failedCount = job.projects.filter(
          (p) => p.status === PROJECT_STATUS.FAILED
        ).length;
        if (payload.etaSeconds != null) job.etaSeconds = payload.etaSeconds;
      });

    case DELETE_ALL_COMPLETED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.etaSeconds = 0;
        job.terminatedAt = Date.now();
      });

    case DELETE_ALL_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Delete failed";
        job.errorCode = payload?.errorCode ?? null;
        job.errorParams = payload?.errorParams ?? null;
        job.terminatedAt = Date.now();
      });

    case DELETE_REQUEST_FAILED:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.active = false;
        job.error = payload?.error || "Delete request failed";
        job.terminatedAt = Date.now();
      });

    case DELETE_JOB_MODAL_TOGGLE:
      return produce(state, (draft) => {
        const jobId = payload?.jobId;
        if (!jobId) return;
        const job = draft.jobs[jobId];
        if (!job) return;
        job.open = payload?.open ?? !job.open;
      });

    case DELETE_JOB_DISMISS:
      return produce(state, (draft) => {
        const jobId = payload?.jobId ?? payload;
        if (jobId === "all") {
          Object.keys(draft.jobs).forEach((id) => {
            if (!draft.jobs[id].active) delete draft.jobs[id];
          });
        } else {
          delete draft.jobs[jobId];
        }
      });

    default:
      return state;
  }
}

export { PROJECT_STATUS };
